/**
 * Organisation-timezone date formatting (rules/01 §6, ADR-0003).
 * Every display goes through these; `.toLocale*String()` is lint-banned in feature code.
 * Output is composed from Intl parts with fixed month names so it is identical on every host.
 *
 * Two kinds of value, two families: an INSTANT (an api `date-time`, "2026-09-30T04:00:00Z") is shown
 * in the organisation's zone by the `formatOrg*` helpers; a calendar DAY (an api `date` column,
 * "2026-09-30") already is an organisation date and is shown as written by `formatDay` /
 * `formatDayRange`, with no zone at all.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/** A calendar day as the api writes one: "2026-09-30". */
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

type DateInput = string | number | Date;

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = formatterCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    formatterCache.set(tz, f);
  }
  return f;
}

interface OrgParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * An instant names its zone: every one the api sends ends in "Z" (`isoDate` is `z.string().datetime()`),
 * and an explicit offset ("+05:30") is as good. A string with neither is not an instant at all — a
 * calendar day ("2026-09-30") or somebody's wall clock ("2026-09-30 14:02:05", a terminal's) — and
 * `new Date()` would read the first as UTC midnight, which is the day BEFORE anywhere west of UTC, and
 * the second in the BROWSER's zone. Found 2026-10-02 on the gym member card: a New York gym saw every
 * "Started" and "Sold until" a day early, and an Indian one (UTC+5:30) never could.
 */
const ZONED_RE = /\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/i;

/** The instant `value` names, or null — a string that names no zone is refused, never read in one. */
function toDate(value: DateInput): Date | null {
  if (typeof value === "string" && !ZONED_RE.test(value)) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Calendar fields of an instant as seen in `tz`. */
function getOrgParts(value: DateInput, tz: string): OrgParts | null {
  const d = toDate(value);
  if (!d) return null;
  const out: Record<string, number> = {};
  for (const p of partsFormatter(tz).formatToParts(d)) {
    if (p.type !== "literal") out[p.type] = Number(p.value);
  }
  return {
    year: out["year"] ?? 0,
    month: out["month"] ?? 1,
    day: out["day"] ?? 1,
    hour: out["hour"] ?? 0,
    minute: out["minute"] ?? 0,
    second: out["second"] ?? 0,
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * "9 Sep 2026". A calendar day ("2026-09-30") is already an organisation date, so it reads as written:
 * the one zone-less string answered rather than refused, because a date is exactly what is asked for.
 * A call site that holds a day says so with `formatDay`; this is the net under the ones that do not.
 * The helpers that also print a time refuse a day ("—"): it has no time of day to print.
 */
export function formatOrgDate(value: DateInput, tz: string): string {
  if (typeof value === "string" && DAY_RE.test(value)) return formatDay(value);
  const p = getOrgParts(value, tz);
  return p ? `${p.day} ${MONTHS[p.month - 1] ?? ""} ${p.year}` : "—";
}

/** "14:02" (24-hour) */
export function formatOrgTime(value: DateInput, tz: string, withSeconds = false): string {
  const p = getOrgParts(value, tz);
  if (!p) return "—";
  return withSeconds ? `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}` : `${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * "17 Sep 22:48" — a stamp for somewhere narrow, where the year is the least useful word in it.
 * Board `1a`'s fleet row writes the last-seen time this way; anywhere the year could matter (a
 * record, an export, a footer) uses `formatOrgDateTime`.
 */
export function formatOrgDayTime(value: DateInput, tz: string): string {
  const p = getOrgParts(value, tz);
  return p ? `${p.day} ${MONTHS[p.month - 1] ?? ""} ${pad(p.hour)}:${pad(p.minute)}` : "—";
}

/** "9 Sep 2026, 14:02" */
export function formatOrgDateTime(value: DateInput, tz: string): string {
  const p = getOrgParts(value, tz);
  return p ? `${formatOrgDate(value, tz)}, ${pad(p.hour)}:${pad(p.minute)}` : "—";
}

/** "2026-09-09" — the organisation's calendar date for `now`; date inputs default from this, never the browser clock. */
export function getOrgTodayString(tz: string, now: DateInput = new Date()): string {
  const p = getOrgParts(now, tz);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)}` : "";
}

/**
 * "2026-09-09" — the organisation calendar date of an instant (seeds date inputs from stored ISO values).
 * A calendar day is its own answer, for the same reason `formatOrgDate` reads one as written.
 */
export function toOrgDateString(value: DateInput, tz: string): string {
  if (typeof value === "string" && DAY_RE.test(value)) return isOrgDayString(value) ? value : "";
  return getOrgTodayString(tz, value);
}

/**
 * "2026-09-08 09:02" — the organisation's wall clock for an instant, as a sortable plain string.
 * Report sheets carry this rather than the UTC instant (M12 §3): a spreadsheet cell has no zone of
 * its own, so the conversion has to happen before the value is written, never in the reader's Excel.
 */
export function toOrgDateTimeString(value: DateInput, tz: string): string | null {
  const p = getOrgParts(value, tz);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}` : null;
}

/**
 * "2026-09-13T15:04:05+05:30" — an instant written in the organisation's zone, offset and all. This is
 * the api's own `orgIso` (apps/api/src/core/orgDate.ts), reproduced here so a sheet built in the browser
 * states its generation time exactly the way the streamed CSV does (rules/01 §7).
 */
export function formatOrgIso(value: DateInput, tz: string): string {
  const d = toDate(value);
  const p = d ? getOrgParts(d, tz) : null;
  if (!d || !p) return "";
  const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const offsetMinutes = Math.round((wall - d.getTime()) / 60_000);
  const abs = Math.abs(offsetMinutes);
  const sign = offsetMinutes < 0 ? "-" : "+";
  return (
    `${String(p.year).padStart(4, "0")}-${pad(p.month)}-${pad(p.day)}` +
    `T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}` +
    `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
}

/**
 * "2026-09-09" typed in the organisation's calendar → the ISO instant of that day's start (00:00:00.000) or
 * end (23:59:59.999) in `tz`. Validity windows are stored as instants (ADR-0003); the terminal applies them itself.
 * Two correction passes settle DST transitions; an impossible date (30 Feb) returns null.
 */
export function orgDateToIso(date: string, tz: string, edge: "start" | "end" = "start"): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const [h, mi, s, ms] = edge === "start" ? [0, 0, 0, 0] : [23, 59, 59, 999];
  const wall = Date.UTC(y, mo - 1, d, h, mi, s);
  let instant = wall;
  for (let pass = 0; pass < 2; pass += 1) {
    const p = getOrgParts(instant, tz);
    if (!p) return null;
    const seen = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    instant = wall - (seen - instant);
  }
  const check = getOrgParts(instant, tz);
  if (!check || check.year !== y || check.month !== mo || check.day !== d) return null;
  return new Date(instant + ms).toISOString();
}

/**
 * **M16 wave 2 (ADR-0021, second addendum): an arbitrary wall-clock time in the organisation's
 * calendar → the ISO instant it names.** `orgDateToIso` answers this for the two edges of a day;
 * this answers it for "13 Sep at 18:30", which is what an expiry picker asks.
 *
 * It matters enough for the ADR to say so in as many words: "a pass issued at 11pm IST that quietly
 * expires on a UTC boundary is a support call". The browser's own timezone is never consulted —
 * `new Date("2026-09-13T18:30")` would parse in the VIEWER's zone, which is wrong for a front desk
 * in Mumbai whose colleague is reviewing from Dubai.
 *
 * Same two correction passes as `orgDateToIso`, for the same reason: a DST transition moves the
 * offset the first guess was made with. `time` is "HH:MM" or "HH:MM:SS"; an impossible date or a
 * wall-clock time that does not exist in `tz` (the hour a spring-forward skips) returns null.
 */
export function orgDateTimeToIso(date: string, time: string, tz: string): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);
  if (!d || !t) return null;
  const [y, mo, day] = [Number(d[1]), Number(d[2]), Number(d[3])];
  const [h, mi, sec] = [Number(t[1]), Number(t[2]), Number(t[3] ?? 0)];
  if (h > 23 || mi > 59 || sec > 59) return null;
  const wall = Date.UTC(y, mo - 1, day, h, mi, sec);
  let instant = wall;
  for (let pass = 0; pass < 2; pass += 1) {
    const p = getOrgParts(instant, tz);
    if (!p) return null;
    const seen = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    instant = wall - (seen - instant);
  }
  const check = getOrgParts(instant, tz);
  if (!check || check.year !== y || check.month !== mo || check.day !== day) return null;
  // A wall-clock time inside a spring-forward gap never happens in `tz`; refuse it rather than
  // silently land an hour away from what the desk typed.
  if (check.hour !== h || check.minute !== mi) return null;
  return new Date(instant).toISOString();
}

/**
 * The calendar day and the wall-clock time an instant reads as in `tz`, as the two strings an
 * `<input type="date">` and an `<input type="time">` hold. The inverse of `orgDateTimeToIso`, so a
 * quick pick can fill the two fields with something the user can then edit.
 */
export function orgDateTimeFields(value: DateInput, tz: string): { date: string; time: string } | null {
  const p = getOrgParts(value, tz);
  if (!p) return null;
  return {
    date: `${String(p.year).padStart(4, "0")}-${pad(p.month)}-${pad(p.day)}`,
    time: `${pad(p.hour)}:${pad(p.minute)}`,
  };
}

/** Relative wording for "last seen" and feeds: no timezone dependence, seconds resolution is deliberately hidden. */
export function formatRelative(value: DateInput, now: DateInput = new Date()): string {
  const d = toDate(value);
  const n = toDate(now);
  if (!d || !n) return "—";
  const diffMs = n.getTime() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;
  const months = Math.round(days / 30);
  return months < 12 ? `${months} mo ago` : `${Math.round(months / 12)} y ago`;
}

const DAY_MS = 86_400_000;

/** Pure calendar arithmetic on "YYYY-MM-DD" strings, no zone involved: the day `n` days after `day`. */
export function addDays(day: string, n: number): string {
  const d = new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** A real calendar day: the engine rolls "2026-02-30" over to March, so the round trip has to come back unchanged. */
export function isOrgDayString(value: string): boolean {
  return DAY_RE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && addDays(value, 0) === value;
}

/** Whole days from `fromDay` to `toDay` ("2026-09-03" → "2026-09-10" is 7; the same day is 0). */
export function daysBetween(fromDay: string, toDay: string): number {
  return Math.round((Date.parse(`${toDay}T00:00:00Z`) - Date.parse(`${fromDay}T00:00:00Z`)) / DAY_MS);
}

export interface OrgDayRange {
  fromDay: string;
  toDay: string;
}

/** The last `days` organisation days ending today (ADR-0003): "last 7 days" is today and the six before it. */
export function lastOrgDays(days: number, tz: string, now: DateInput = new Date()): OrgDayRange {
  const toDay = getOrgTodayString(tz, now);
  return { fromDay: addDays(toDay, -(days - 1)), toDay };
}

/**
 * Keep a typed range inside a caller's bound: the end on or after the start, at most `maxDays` apart.
 * `moved` names the end the reader just changed, so the other one gives way rather than the input
 * snapping back under their fingers (the event log's rule, M4 §4; shared by the core reports' range
 * filter and every module's own bounded-range reports, e.g. the ledger's).
 */
export function clampRange(range: OrgDayRange, moved: "from" | "to", maxDays: number): OrgDayRange {
  let { fromDay, toDay } = range;
  if (daysBetween(fromDay, toDay) < 0) {
    if (moved === "from") toDay = fromDay;
    else fromDay = toDay;
  }
  if (daysBetween(fromDay, toDay) >= maxDays) {
    if (moved === "from") toDay = addDays(fromDay, maxDays - 1);
    else fromDay = addDays(toDay, -(maxDays - 1));
  }
  return { fromDay, toDay };
}

/** An organisation-day range as the ISO instants the API takes: start of the first day, end of the last, in `tz`. */
export function orgDayRangeToIso(range: OrgDayRange, tz: string): { from: string; to: string } | null {
  const from = orgDateToIso(range.fromDay, tz, "start");
  const to = orgDateToIso(range.toDay, tz, "end");
  return from && to ? { from, to } : null;
}

/**
 * "30 Sep 2026" from "2026-09-30": one calendar day (an api `date` column — a membership's `startsOn`,
 * a ledger row's `day`) as written, no zone involved. Anything that is not a real day reads "—".
 */
export function formatDay(day: string): string {
  if (!isOrgDayString(day)) return "—";
  const [y = 0, m = 1, d = 1] = day.split("-").map(Number);
  return `${d} ${MONTHS[m - 1] ?? ""} ${y}`;
}

/** "3–10 Sep 2026", "28 Aug – 10 Sep 2026", "10 Sep 2026" — from day strings, no zone involved. */
export function formatDayRange(range: OrgDayRange): string {
  const a = range.fromDay.split("-").map(Number);
  const b = range.toDay.split("-").map(Number);
  const [ay = 0, am = 1, ad = 1] = a;
  const [by = 0, bm = 1, bd = 1] = b;
  const monthA = MONTHS[am - 1] ?? "";
  const monthB = MONTHS[bm - 1] ?? "";
  if (range.fromDay === range.toDay) return `${bd} ${monthB} ${by}`;
  if (ay === by && am === bm) return `${ad}–${bd} ${monthB} ${by}`;
  if (ay === by) return `${ad} ${monthA} – ${bd} ${monthB} ${by}`;
  return `${ad} ${monthA} ${ay} – ${bd} ${monthB} ${by}`;
}

/** "09:00–10:00" for hour 9 of the organisation day. */
export function formatHourRange(hour: number): string {
  return `${pad(hour)}:00–${pad((hour + 1) % 24)}:00`;
}
